import React, { useEffect, useMemo, useState } from 'react';
import type { Order } from '../types/myaccount';
import api from '../services/api';
import { ChartSkeleton } from './MyAccountLoadingStates';
import MyAccountCard from './MyAccountCard';

interface MyAccountMonthlyOrdersChartProps {
    userName?: string;
}

interface MonthlyOrderData {
    month: string;
    total: number;
}

const MyAccountMonthlyOrdersChart: React.FC<MyAccountMonthlyOrdersChartProps> = ({ userName = 'User' }) => {
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                setError(null);
                const dashboardData = await api.getAccountDashboardData();
                setOrders(Array.isArray(dashboardData.recent_orders) ? dashboardData.recent_orders : []);
            } catch (err) {
                console.error('Error fetching monthly orders chart data:', err);
                setError('Failed to load monthly chart.');
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const monthlyData = useMemo<MonthlyOrderData[]>(() => {
        const monthlyMap: { [key: string]: number } = {};

        for (let i = 11; i >= 0; i -= 1) {
            const date = new Date();
            date.setMonth(date.getMonth() - i);
            const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            monthlyMap[monthKey] = 0;
        }

        (orders || []).forEach((order) => {
            const orderDate = new Date(order.date_created);
            const monthKey = orderDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            if (Object.prototype.hasOwnProperty.call(monthlyMap, monthKey)) {
                monthlyMap[monthKey] = (monthlyMap[monthKey] || 0) + parseFloat(order.total);
            }
        });

        return Object.keys(monthlyMap).map((month) => ({
            month,
            total: monthlyMap[month] || 0,
        }));
    }, [orders]);

    if (loading && !orders) {
        return (
            <MyAccountCard
                slug="monthly-orders"
                title={<span className="skeleton d-block" style={{ height: '24px', width: '32%' }} />}
                subtitle={<span className="skeleton d-block" style={{ height: '14px', width: '24%' }} />}
            >
                <ChartSkeleton showHeader={false} />
            </MyAccountCard>
        );
    }

    if (error) {
        return (
            <MyAccountCard slug="monthly-orders" title="Monthly Orders">
                <p className="error-message">{error}</p>
            </MyAccountCard>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <MyAccountCard slug="monthly-orders" title="Monthly Orders">
                <div className="woocommerce-message woocommerce-message--info woocommerce-Message woocommerce-Message--info woocommerce-info">
                    No orders yet.
                </div>
            </MyAccountCard>
        );
    }

    const totals = monthlyData.map((data) => data.total);
    const maxValue = Math.max(...totals, 1);
    const chartLeftInset = 56;
    const rangeLabel = monthlyData.length > 0
        ? `${monthlyData[0]?.month || ''} - ${monthlyData[monthlyData.length - 1]?.month || ''}`
        : '';

    return (
        <MyAccountCard
            slug="monthly-orders"
            title={<>Monthly Orders <span className="monthly-orders-chart__account">({userName})</span></>}
            subtitle={rangeLabel}
            titleClassName="monthly-orders-chart__title"
        >
            <div style={{ position: 'relative', height: '300px', paddingLeft: `${chartLeftInset}px` }}>
                <div style={{ position: 'absolute', left: '0', top: '0', bottom: '40px', width: `${chartLeftInset - 10}px`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '12px', color: 'var(--weka-muted-text-color)' }}>
                    <div>{Math.round(maxValue).toLocaleString()}</div>
                    <div>{Math.round(maxValue / 2).toLocaleString()}</div>
                    <div>0</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '260px', borderBottom: '1px solid var(--weka-card-border)', gap: '8px' }}>
                    {monthlyData.map((data) => {
                        const height = maxValue > 0 ? (data.total / maxValue) * 240 : 0;
                        return (
                            <div key={data.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', position: 'relative' }}>
                                <div
                                    style={{
                                        width: '100%',
                                        height: `${height}px`,
                                        backgroundColor: 'var(--weka-info-color)',
                                        borderRadius: '4px 4px 0 0',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        minHeight: data.total > 0 ? '4px' : '0',
                                    }}
                                    title={`${data.month}: $${data.total.toFixed(2)}`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', gap: '8px' }}>
                    {monthlyData.map((data) => (
                        <div key={data.month} style={{ flex: 1, fontSize: '11px', color: 'var(--weka-muted-text-color)', textAlign: 'center' }}>
                            {data.month}
                        </div>
                    ))}
                </div>
            </div>
        </MyAccountCard>
    );
};

export default MyAccountMonthlyOrdersChart;
